import {
  AutoPilotConfiguration,
  AutoPilotSendWindow,
} from "../../../../constants/types";

/** The limits of documentations/AUTO_PILOT_FEATURE.md §1, also checked by the server. */
export const AUTO_PILOT_LIMITS = {
  descriptionMin: 200,
  descriptionMax: 1000,
  toneExamplesMin: 1,
  toneExamplesMax: 15,
  toneExampleMaxLength: 280,
  offersMin: 1,
  offersMax: 5,
  offerMaxLength: 60,
  capMin: 1,
  capMax: 50,
} as const;

export const WEEK_DAYS = [1, 2, 3, 4, 5, 6, 7];

export const AUTO_PILOT_LANGUAGES = [
  "fr",
  "en",
  "de",
  "it",
  "es",
  "pt",
  "ru",
  "ja",
  "zh",
  "ar",
];

/** As the server does: the chosen language if it is one it accepts, else the owner's, unknown while still loading. */
export const autoPilotLanguage = (
  chosen: string | undefined,
  ownerLanguage: string | undefined
): string | undefined =>
  chosen && AUTO_PILOT_LANGUAGES.includes(chosen) ? chosen : ownerLanguage;

export const DEFAULT_SEND_WINDOW: AutoPilotSendWindow = {
  start: "09:00",
  end: "20:00",
  days: WEEK_DAYS,
};

/** Stored settings completed with the defaults, keeping whatever else the stored object carries. */
export const withAutoPilotDefaults = (
  stored?: Partial<AutoPilotConfiguration>
): AutoPilotConfiguration => ({
  ...stored,
  enabled: stored?.enabled === true,
  description: stored?.description ?? "",
  promotional_messages: stored?.promotional_messages?.length
    ? stored.promotional_messages
    : [""],
  allow_promotion: stored?.allow_promotion === true,
  promotion_messages: stored?.promotion_messages ?? [],
  promotion_interval_value: stored?.promotion_interval_value ?? 1,
  promotion_interval_unit: stored?.promotion_interval_unit ?? "day",
  send_window: stored?.send_window ?? DEFAULT_SEND_WINDOW,
});

export const filled = (texts: string[]) =>
  texts.filter((text) => text.trim() !== "");

export type AutoPilotProblem =
  | "descriptionTooShort"
  | "descriptionTooLong"
  | "minPromotionalMessages"
  | "maxPromotionalMessages"
  | "minPromotionMessages"
  | "maxPromotionMessages"
  | "intervalInvalid"
  | "sendWindowOrder"
  | "sendWindowTooShort"
  | "sendWindowNoDay";

const minutesOf = (hhmm: string) => {
  const [hours, minutes] = hhmm.split(":").map(Number);
  return hours * 60 + minutes;
};

/** KMM sends in the last 30 minutes of the window, so the server asks for one hour at least. */
export const SEND_WINDOW_MIN_MINUTES = 60;

export const sendWindowProblem = (
  sendWindow: AutoPilotSendWindow
): "sendWindowOrder" | "sendWindowTooShort" | "sendWindowNoDay" | null => {
  if (
    !sendWindow.start ||
    !sendWindow.end ||
    sendWindow.start >= sendWindow.end
  )
    return "sendWindowOrder";
  if (
    minutesOf(sendWindow.end) - minutesOf(sendWindow.start) <
    SEND_WINDOW_MIN_MINUTES
  )
    return "sendWindowTooShort";
  if (sendWindow.days.length === 0) return "sendWindowNoDay";
  return null;
};

/** The first rule the settings break, named by its translation key under `dashboard.features.lmc.autoPilot`. */
export const autoPilotProblem = (
  config: AutoPilotConfiguration
): AutoPilotProblem | null => {
  const limits = AUTO_PILOT_LIMITS;
  if (config.description.length < limits.descriptionMin)
    return "descriptionTooShort";
  if (config.description.length > limits.descriptionMax)
    return "descriptionTooLong";

  const examples = filled(config.promotional_messages).length;
  if (examples < limits.toneExamplesMin) return "minPromotionalMessages";
  if (examples > limits.toneExamplesMax) return "maxPromotionalMessages";

  if (config.allow_promotion) {
    const offers = filled(config.promotion_messages).length;
    if (offers < limits.offersMin) return "minPromotionMessages";
    if (offers > limits.offersMax) return "maxPromotionMessages";
  }

  const cap = config.promotion_interval_value;
  if (
    !Number.isInteger(cap) ||
    (cap as number) < limits.capMin ||
    (cap as number) > limits.capMax
  )
    return "intervalInvalid";

  return sendWindowProblem(config.send_window ?? DEFAULT_SEND_WINDOW);
};

/** What is sent: no empty text, offers only when allowed, days in order, and a language only once chosen or stored, the server falling back on the owner's country otherwise. */
export const autoPilotToSave = (
  config: AutoPilotConfiguration,
  ownerLanguage?: string
): AutoPilotConfiguration => {
  const { language: chosen, ...rest } = config;
  const language =
    chosen === undefined ? undefined : autoPilotLanguage(chosen, ownerLanguage);
  const sendWindow = config.send_window ?? DEFAULT_SEND_WINDOW;
  return {
    ...rest,
    ...(language && { language }),
    promotional_messages: filled(config.promotional_messages),
    promotion_messages: config.allow_promotion
      ? filled(config.promotion_messages)
      : [],
    send_window: {
      ...sendWindow,
      days: WEEK_DAYS.filter((day) => sendWindow.days.includes(day)),
    },
  };
};
